import React, { useState, useEffect, CSSProperties, useRef } from "react";
import { Table, OverlayTrigger, Tooltip, Dropdown } from "react-bootstrap";

import { useDispatch } from "react-redux";
import {
  fetchEditorModelPopupDetails,
  getDWCExtensionLogs,
} from "../../../../services/EditorWorkFlowAPI";
import {
  CASE_STAGE_STATUS,
  EDITOR_REPORT_STATUS,
  QME_APPOINTMENT_STATUS,
  TRANSCRIPTION_REPORT_STATUS,
} from "../../../../common/Constant";
import {
  setIsDashboard,
  setNewCaseInFoID,
} from "../../../../redux/slice/CaseHeaderSlice";
import { DropdownPortal } from "../../../../common/DropdownPortal";
import DictationModal from "../../Editor/components/DictationModel";
import {
  GetAssignedTo,
  updateAssignedTo,
} from "../../../../services/DoctorWorkflowAPI";
import { formatAppointmentDateTime } from "../../../../utils/dateUtils";
import FloatingTableModal from "../../../../common/FloatingTableModal";
import { DWC_LOG_HEADERS } from "../../Editor/actions/constant";

// Define the DataTableProps interface
interface DataTableProps {
  headers: {
    key: string;
    label: string;
    sortable?: boolean;
    items?: any;
    style?: any;
    isNumeric?: boolean;
  }[];
  rows?: any[];
  onAction?: (action: string, row: any) => void;
  onSortAction?: (row: any, action?: string) => void;

  isLogTable?: boolean;

  selectedRow?: any;

  isCheckbox?: boolean;
  onCheckboxSelect?: (selectedRows: any[]) => void;
  selectedRowIds?: any;
  disabled?: boolean;
  onSaveSuccess?: any;
  selectAll?: boolean; // ✅ just declare it, no assignment
  setSelectAll?: (value: boolean) => void;
  filteredActionOptions?: any;
  onCaseClick?: any;
  activeListName?: string;
}

const DoctorDataTable: React.FC<DataTableProps> = ({
  headers,
  rows = [],
  onAction,
  onSortAction,

  isLogTable = false,

  isCheckbox = false,
  onCheckboxSelect,
  selectedRowIds = [],
  disabled = false,
  onSaveSuccess,
  selectAll = false,
  setSelectAll = () => { },
  filteredActionOptions,
  onCaseClick,
  activeListName,
}) => {
  const [sortedRows, setSortedRows] = useState<any[]>(rows);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  // const [selectAll, setSelectAll] = useState(false);

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "ASC" | "DESC" | null;
  }>({
    key: "",
    direction: null,
  });
  const [openDropdownRowId, setOpenDropdownRowId] = useState<number | null>(
    null,
  );
  const [assignMedicareAssistantOptions, setassignMedicareAssistantOptions] =
    useState<any[]>([]);

  const dispatch = useDispatch();
  const [visibleHeaderIndex, setVisibleHeaderIndex] = useState<boolean>(false);
  const [logRows, setLogRows] = useState<any[]>([]);
  const [modalTitle, setModalTitle] = useState<string>("");
  const [showModalLogs, setShowModalLogs] = useState(false);
  const [modalPosLog, setModalPosLog] = useState<{
    top: number;
    left?: number;
    right?: number;
  } | null>(null);

  const handleDropdownToggle = async (
    rowId: number,
    row: any,
    isOpen: boolean,
  ) => {
    setOpenDropdownRowId(isOpen ? rowId : null);
    const responseData = await GetAssignedTo();
    if (responseData?.isSuccess && Array.isArray(responseData.result)) {
      const formattedOptions = responseData.result.map((item: any) => ({
        value: item.userId,
        label: item.fullName,
      }));
      setassignMedicareAssistantOptions(formattedOptions);
    }
  };

  useEffect(() => {
    if (rows && rows.length > 0) {
      (async () => {
        try {
          const stakeholderIds = [
            ...new Set(rows.map((r) => r.stakeholderId).filter(Boolean)),
          ];

          const allOptions: any[] = [];

          for (const id of stakeholderIds) {
            const responseData = await GetAssignedTo();
            if (responseData?.isSuccess && Array.isArray(responseData.result)) {
              const formattedOptions = responseData.result.map((item: any) => ({
                value: item.userId,
                label: item.fullname,
              }));
              allOptions.push(...formattedOptions);
            }
          }

          // store combined options in state
          setassignMedicareAssistantOptions(allOptions);
        } catch (err) {
          console.error("Error fetching assistants:", err);
        }
      })();
    }
  }, [rows]);

  const handleEditorChange = async (row: any, option: any) => {
    const payload = {
      subjectQmeScheduleIDs: String(row.subjectQmeScheduleId),
      assignTo: Number(option),
      editorAssignedUserId: Number(option),
      editorAssignedDate: null,
      editorComments: "",
    };
    try {
      const response = await updateAssignedTo(payload);
      if (response.isSuccess) {
        setFocusedRowId(null);
        setTimeout(() => {
          onSaveSuccess();
        }, 800);
      }
    } catch (error) {
      console.error("Error updating assistant:", error);
    }
  };

  const handleSort = (key: string, direction: "ASC" | "DESC") => {
    if (sortConfig.key === key && sortConfig.direction === direction) {
      return; // Prevent clicking on the same active sort direction
    }
    const sorted = [...sortedRows].sort((a, b) => {
      if (direction === "ASC") {
        return a[key] > b[key] ? 1 : -1;
      }
      if (direction === "DESC") {
        return a[key] < b[key] ? 1 : -1;
      }
      return 0;
    });

    setSortedRows(sorted);
    setSortConfig({ key, direction });
    onSortAction?.({ key, direction });
  };

  useEffect(() => {
    if (
      isCheckbox &&
      rows?.length > 0 &&
      selectedRowIds?.length > 0 &&
      Array.isArray(selectedRowIds)
    ) {
      const cleanedIds = selectedRowIds.map((id: string) => id.trim());
      const selectedIdSet = new Set(cleanedIds);

      const selected = rows.filter((row) =>
        selectedIdSet.has(String(row.subjectQmeScheduleId)),
      );

      setSortedRows(rows);
      setSelectedRows(selected);

      const validRows = rows.filter(
        (row) => !NON_SELECTABLE_STAGES.includes(row?.caseStage),
      );

      const allSelected =
        validRows.length > 0 && selected.length === validRows.length;

      setSelectAll(allSelected);
    } else {
      setSortedRows(rows || []);
      setSelectedRows([]);
      setSelectAll(false); // 🔑 ensure reset
    }
  }, [rows, selectedRowIds]);

  const NON_SELECTABLE_STAGES = [
    CASE_STAGE_STATUS.CLOSED,
    CASE_STAGE_STATUS.CANCELLED,
  ];

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;

    const attemptedRows = [...sortedRows];

    // Rows that are actually selectable
    const selectableRows = sortedRows.filter(
      (row) =>
        row.appointmentStatusDesc !== EDITOR_REPORT_STATUS.COMPLETED &&
        !NON_SELECTABLE_STAGES.includes(row?.caseStage),
    );

    if (selectableRows.length === 0) {
      setSelectAll(false);
      setSelectedRows([]); // keep UI clean
      onCheckboxSelect?.(attemptedRows);
      return;
    }

    // Normal flow
    const updated = newSelectAll ? selectableRows : [];

    setSelectAll(newSelectAll);
    setSelectedRows(updated);
    onCheckboxSelect?.(updated);
  };

  const handleCheckboxChange = (row: any) => {
    const isSelected = selectedRows.some(
      (r) => r.subjectQmeScheduleId === row.subjectQmeScheduleId,
    );

    const updated = isSelected
      ? selectedRows.filter(
        (r) => r.subjectQmeScheduleId !== row.subjectQmeScheduleId,
      )
      : [...selectedRows, row];

    setSelectedRows(updated);
    onCheckboxSelect?.(updated);
  };

  const dropdownRefMap = useRef<{ [key: string]: HTMLElement | null }>({});
  const [focusedRowId, setFocusedRowId] = useState<number | null>(null);

  const [selectedMedicalAssistants, setSelectedMedicalAssistants] = useState<{
    [id: number]: number | null;
  }>({});

  useEffect(() => {
    const initialSelections: { [id: number]: number | null } = {};

    sortedRows.forEach((row) => {
      initialSelections[row.subjectQmeScheduleId] =
        row.assignedToUserId ?? row.medicalAssistantId ?? null;
    });

    setSelectedMedicalAssistants(initialSelections);
  }, [sortedRows]);

  const [searchValues, setSearchValues] = useState<{ [id: number]: string }>(
    {},
  );

  const [showModal, setShowModal] = useState(false);
  const [instructionsData, setInstructionsData] = useState<any[]>([]);
  const [selectedRowData, setSelectedRowData] = useState<any[]>([]);
  const [modalPos, setModalPos] = useState<{
    top: number;
    left?: number;
    right?: number;
  } | null>(null);

  const fetchEditorModelPopupDetailsList = async (Id: Number) => {
    try {
      const data = await fetchEditorModelPopupDetails(Id);
      setInstructionsData(data?.result || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleOpenDictationModal = async (row: any, e: React.MouseEvent) => {
    setSelectedRowData(row);
    await fetchEditorModelPopupDetailsList(Number(row?.subjectQmeScheduleId));
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const modalWidth = window.innerWidth * 0.5; // since modal width is 50%
    const verticalOffset = 5; // small space below icon
    const horizontalOffset = 18; // new slight horizontal offset

    let position: { top: number; left?: number; right?: number };

    if (rect.left < window.innerWidth / 2) {
      // icon on left side → open modal slightly to the right
      position = {
        top: rect.bottom + verticalOffset,
        left: rect.left + horizontalOffset,
      };
    } else {
      // icon on right side → open modal slightly to the left
      position = {
        top: rect.bottom + verticalOffset,
        left: rect.right - modalWidth - horizontalOffset,
      };
    }

    setModalPos(position);
    setShowModal(true);
  };

  const handleDWCStatusLog = async (row: any, e: React.MouseEvent) => {
    try {
      const listData: any[] = await getDWCExtensionLogs(
        Number(row?.subjectQmeScheduleId),
      );

      const formattedLogs = listData?.map((item: any) => ({
        status: item?.newStatusName ?? "-",
        who: item?.who ?? "-",
        role: item?.userRoleName ?? "-",
        when: item?.when ? formatAppointmentDateTime(item.when) : "-",
      }));

      setLogRows(formattedLogs);
    } catch (error) {
      console.error("Error fetching logs data:", error);
    }

    setModalTitle("Logs");

    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const modalWidth = window.innerWidth * 0.5;
    const verticalOffset = 5;
    const horizontalOffset = 18;
    let position: { top: number; left?: number; right?: number };
    if (rect.left < window.innerWidth / 2) {
      position = {
        top: rect.bottom + verticalOffset,
        left: rect.left + horizontalOffset,
      };
    } else {
      position = {
        top: rect.bottom + verticalOffset,
        left: rect.right - modalWidth - horizontalOffset,
      };
    }

    setModalPosLog(position);
    setShowModalLogs(true);
  };

  const modalProps = {
    mode: "table" as const,
    data: logRows,
    headers: DWC_LOG_HEADERS,
  };

  return (
    <>
      <div className="table-responsive border-0 mb-2">
        <Table style={{ tableLayout: "fixed" }} className="table  newtab">
          <thead>
            <tr>
              {isCheckbox && sortedRows?.length > 0 && (
                <th style={{ width: "45px", textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    disabled={disabled || filteredActionOptions?.length == 0}
                  />
                </th>
              )}

              {headers.map((header) => (
                // <th key={header.key} style={thStyle}>
                <th
                  key={header.key}
                  className="adjust_ma"
                  style={{
                    border: "none",
                    padding: "8px",
                    backgroundColor: "#e0f7fa",
                    color: "#000",
                    cursor: header.sortable ? "pointer" : "default",
                    position: "sticky",
                    top: 0,
                    zIndex: -90,

                    textAlign: ["dictationId"].includes(header.key)
                      ? "center"
                      : "left",
                    ...header.style,
                  }}
                >
                  <div
                    style={{
                      display: "flex",

                      justifyContent: ["dictationId"].includes(header.key)
                        ? "space-evenly"
                        : "space-between",

                      alignItems: "center",
                    }}
                  >
                    <span>{header.label}</span>
                    {header.sortable && (
                      <span
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          marginLeft: "4px",
                          cursor: "pointer",
                          position: "absolute",
                          right: "0px",
                        }}
                      >
                        {!(
                          sortConfig.key === header.key &&
                          sortConfig.direction === "ASC"
                        ) && (
                            <span
                              className="material-icons"
                              style={{
                                fontSize: "18px",
                                lineHeight: "10px",
                              }}
                              onClick={() => handleSort(header.key, "ASC")}
                            >
                              arrow_drop_up
                            </span>
                          )}
                        {!(
                          sortConfig.key === header.key &&
                          sortConfig.direction === "DESC"
                        ) && (
                            <span
                              className="material-icons"
                              style={{
                                fontSize: "18px",
                                lineHeight: "10px",
                              }}
                              onClick={() => handleSort(header.key, "DESC")}
                            >
                              arrow_drop_down
                            </span>
                          )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows?.length > 0 ? (
              sortedRows.map((row, index) => (
                <tr
                  key={row.id}
                  style={{
                    backgroundColor: index % 2 === 1 ? "white" : "#f2f2f2",
                  }}
                >
                  {isCheckbox && (
                    <td style={{ textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={selectedRows.some(
                          (r) =>
                            r.subjectQmeScheduleId === row.subjectQmeScheduleId,
                        )}
                        onChange={() => handleCheckboxChange(row)}
                      />
                    </td>
                  )}

                  {headers.map((header) => {
                    const cellContent = row[header.key];

                    if (header.key === "patientName") {
                      const cellContent = row[header.key];
                      return (
                        <td key={header.key}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <OverlayTrigger
                              placement="auto"
                              delay={{ show: 200, hide: 10 }}
                              overlay={
                                cellContent && cellContent?.length > 15 ? (
                                  <Tooltip
                                    id={`tooltip-${header.key}-${row.id}`}
                                    style={{ backgroundColor: "#b3e0ff" }}
                                  >
                                    {cellContent}
                                  </Tooltip>
                                ) : (
                                  <></>
                                )
                              }
                            >
                              <span
                                className="d-inline-block text-truncate"
                                style={{
                                  // maxWidth: "calc(100% - 8px)",
                                  cursor: "pointer",
                                  width: "95%",
                                  color: "#007bff",
                                }}
                              >
                                <a
                                  href="#/search-case"
                                  style={{
                                    textDecoration: "none",
                                    color: "inherit",
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    onCaseClick(row);
                                  }}
                                >
                                  {cellContent}
                                </a>
                              </span>
                            </OverlayTrigger>
                          </div>
                        </td>
                      );
                    }

                    if (header.key === "transcriptionFileName") {
                      const cellContent = row[header.key];
                      return (
                        <td key={header.key}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "80%",
                            }}
                          >
                            <OverlayTrigger
                              placement="auto"
                              delay={{ show: 200, hide: 10 }}
                              overlay={
                                cellContent && cellContent?.length > 15 ? (
                                  <Tooltip
                                    id={`tooltip-${header.key}-${row.id}`}
                                    style={{ backgroundColor: "#b3e0ff" }}
                                  >
                                    {cellContent}
                                  </Tooltip>
                                ) : (
                                  <></>
                                )
                              }
                            >
                              <span
                                className="d-inline-block text-truncate"
                                style={{
                                  // maxWidth: "calc(100% - 8px)",
                                  cursor: "pointer",
                                  width: "95%",
                                  color: "#007bff",
                                }}
                              >
                                <span
                                  onClick={(e) => {
                                    onAction?.(row, header.key);
                                  }}
                                >
                                  {cellContent}
                                </span>
                              </span>
                            </OverlayTrigger>
                          </div>
                        </td>
                      );
                    }

                    if (header.key === "dwcStatus") {
                      const showLogIcon = [
                        "Requested",
                        "Approved",
                        "Denied",
                      ].includes(row?.dwcStatus);

                      return (
                        <td key={header.key}>
                          {row?.dwcStatus}
                          {showLogIcon && (
                            <span
                              className="material-symbols-outlined"
                              title="Logs"
                              onClick={(e) => handleDWCStatusLog(row, e)}
                              style={{
                                padding: "8px",
                                cursor: " pointer",
                                color: "#007bff",
                              }}
                            >
                              description
                            </span>
                          )}
                        </td>
                      );
                    }

                    if (header.key === "dictationId") {
                      const isDisabled = row.isDictationAvailable === 0;

                      return (
                        <td
                          key={header.key}
                          style={{
                            padding: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <span
                            className={`material-symbols-outlined dicticonicon ${isDisabled ? "dicticon-disabled" : ""
                              }`}
                            onClick={
                              isDisabled
                                ? undefined
                                : (e) => handleOpenDictationModal(row, e)
                            }
                          >
                            mic
                          </span>
                        </td>
                      );
                    }

                    const isStatusColumn = header.key === "reportStatus";

                    const getStatusColor = (status: string) => {
                      switch (status) {
                        case EDITOR_REPORT_STATUS.OVERDUE:
                        case EDITOR_REPORT_STATUS.EDITING_NOT_INITIATED:
                          return "#FF0000";
                        case EDITOR_REPORT_STATUS.PENDING_DOCTOR_APPROVAL:
                          return "#0092d5";
                        case EDITOR_REPORT_STATUS.EDITING_PENDING:
                          return "#197a72ff";
                        case EDITOR_REPORT_STATUS.EDITING_IN_PROGRESS:
                          return "#ffc117ff";
                        case EDITOR_REPORT_STATUS.SENT_FOR_REWORK:
                          return "#3a17ffff";
                        case EDITOR_REPORT_STATUS.APPROVED_BY_DOCTOR:
                          return "#025f3bff";
                        case EDITOR_REPORT_STATUS.EXCEPTION:
                          return "#b40d69ff";
                        case EDITOR_REPORT_STATUS.COMPLETED:
                          return "#0E8536";
                        case TRANSCRIPTION_REPORT_STATUS.TRANSCRIPTION_PENDING:
                          return "#710e85";
                        case TRANSCRIPTION_REPORT_STATUS.TRANSCRIPTION_IN_PROGRESS:
                          return "#022bb3";
                        default:
                          return "#000000"; // black (default)
                      }
                    };

                    const inputValue =
                      searchValues[row.subjectQmeScheduleId] !== undefined
                        ? searchValues[row.subjectQmeScheduleId] ||
                        row.assignedTo ||
                        "Select"
                        : row.assignedTo || "Select";

                    const tooltipId = `tooltip-assignedTo-${row.subjectQmeScheduleId}`;

                    return (
                      <td
                        key={header.key}
                        style={{
                          padding: header.isNumeric
                            ? "8px 20px 8px 8px !important"
                            : "8px",
                          textAlign: header.isNumeric ? "center" : "left",
                        }}
                        className="reports_wrapper"
                      >
                        {header.key === "assignedTo" ? (
                          <>
                            <input
                              type="text"
                              ref={(el) => {
                                if (
                                  openDropdownRowId === row.subjectQmeScheduleId
                                ) {
                                  dropdownRefMap.current[
                                    row.subjectQmeScheduleId
                                  ] = el;
                                }
                              }}
                              className={`ellipsis-input ${focusedRowId === row.subjectQmeScheduleId
                                  ? ""
                                  : "appointment_input"
                                }`}
                              value={inputValue}
                              title={inputValue}
                              data-tooltip-id={tooltipId}
                              disabled={
                                row.reportStatus ===
                                EDITOR_REPORT_STATUS.COMPLETED ||
                                row.reportStatus ===
                                EDITOR_REPORT_STATUS.SENT_FOR_REWORK ||
                                row.reportStatus ===
                                EDITOR_REPORT_STATUS.PENDING_DOCTOR_APPROVAL ||
                                row.reportStatus ===
                                EDITOR_REPORT_STATUS.APPROVED_BY_DOCTOR ||
                                row.caseStage === CASE_STAGE_STATUS.CANCELLED ||
                                row.caseStage === CASE_STAGE_STATUS.CLOSED ||
                                row.reportStatus ===
                                EDITOR_REPORT_STATUS.EDITING_IN_PROGRESS ||
                                (row.reportStatus ===
                                  EDITOR_REPORT_STATUS.EDITING_NOT_INITIATED &&
                                  row.appointmentStatus !=
                                  QME_APPOINTMENT_STATUS.COMPLETED) ||
                                ((row.reportStatus ===
                                  EDITOR_REPORT_STATUS.EDITING_PENDING ||
                                  row.reportStatus ===
                                  EDITOR_REPORT_STATUS.EDITING_NOT_INITIATED) &&
                                  row.isDictationAvailable === 1) ||
                                row.reportStatus ===
                                TRANSCRIPTION_REPORT_STATUS.TRANSCRIPTION_PENDING ||
                                row.reportStatus ===
                                TRANSCRIPTION_REPORT_STATUS.TRANSCRIPTION_IN_PROGRESS
                              }
                              style={{
                                cursor: "pointer",
                                color: "#000",
                                backgroundColor: "#fff",
                                border: "1px solid #ced4da",
                                borderRadius: "4px",
                                padding: "4px 8px",
                                width: "100%",
                              }}
                              onClick={() => {
                                setFocusedRowId(row.subjectQmeScheduleId);
                                handleDropdownToggle(
                                  row.subjectQmeScheduleId,
                                  row,
                                  openDropdownRowId !==
                                  row.subjectQmeScheduleId,
                                );
                              }}
                              onChange={(e) => {
                                const typed = e.target.value;
                                setSearchValues((prev) => ({
                                  ...prev,
                                  [row.subjectQmeScheduleId]: typed,
                                }));
                              }}
                            />

                            {inputValue?.length > 15 && (
                              <Tooltip
                                id={tooltipId}
                                style={{ backgroundColor: "#b3e0ff" }}
                              >
                                {inputValue}
                              </Tooltip>
                            )}

                            {/* Icon appended when focused */}
                            {focusedRowId === row.subjectQmeScheduleId && (
                              <span className="arrowpointer_appoitnment">
                                &nbsp;
                              </span>
                            )}
                            <DropdownPortal
                              visible={
                                openDropdownRowId === row.subjectQmeScheduleId
                              }
                              targetRef={{
                                current:
                                  dropdownRefMap.current[
                                  row.subjectQmeScheduleId
                                  ] || null,
                              }}
                              onClose={() => {
                                setOpenDropdownRowId(null);
                                setSearchValues((prev) => ({
                                  ...prev,
                                  [row.subjectQmeScheduleId]: "",
                                }));
                              }}
                            >
                              {(() => {
                                const filteredOptions =
                                  assignMedicareAssistantOptions?.filter(
                                    (option: any) =>
                                      (
                                        searchValues[
                                        row.subjectQmeScheduleId
                                        ] || ""
                                      )
                                        ?.toLowerCase()
                                        ?.trim()
                                        ?.split(" ")
                                        .every((word) =>
                                          option.label
                                            .toLowerCase()
                                            .includes(word),
                                        ),
                                  );

                                if (filteredOptions.length === 0) {
                                  return (
                                    <div
                                      style={{
                                        fontSize: "13px",
                                        padding: "5px",
                                      }}
                                      className="no_rec_data"
                                    >
                                      No Match Found
                                    </div>
                                  );
                                }

                                return filteredOptions.map((option: any) => (
                                  <Dropdown.Item
                                    key={option.value}
                                    as="label"
                                    active={
                                      option.value === row.subjectQmeScheduleId
                                    }
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      cursor: "pointer",
                                    }}
                                    onClick={() => {
                                      handleEditorChange(row, option.value);
                                      setSelectedMedicalAssistants((prev) => ({
                                        ...prev,
                                        [row.subjectQmeScheduleId]:
                                          option.value,
                                      }));
                                      setOpenDropdownRowId(null);
                                      setSearchValues((prev) => ({
                                        ...prev,
                                        [row.subjectQmeScheduleId]: "",
                                      }));
                                    }}
                                  >
                                    <input
                                      type="radio"
                                      name={`medicalAssistant_${row.subjectQmeScheduleId}`}
                                      checked={
                                        selectedMedicalAssistants[
                                        row.subjectQmeScheduleId
                                        ] === option.value
                                      }
                                      style={{ pointerEvents: "none" }}
                                    />
                                    {option.label}
                                  </Dropdown.Item>
                                ));
                              })()}
                            </DropdownPortal>
                          </>
                        ) : header.key === "locations" ? (
                          // 🔹 Custom render for locations (city + zip)
                          <span>
                            {row.locationCity}, {row.locationZip}
                          </span>
                        ) : header.key === "lodictationIdcations" ? (
                          // 🔹 Custom render for locations (city + zip)
                          <span>
                            {row.locationCity}, {row.locationZip}
                          </span>
                        ) : (
                          <OverlayTrigger
                            placement="auto"
                            delay={{ show: 200, hide: 10 }}
                            overlay={
                              cellContent &&
                                cellContent?.length > (isLogTable ? 60 : 15) ? (
                                <Tooltip
                                  id={`tooltip-${header.key}-${row.id}`}
                                  style={{ backgroundColor: "#b3e0ff" }}
                                >
                                  {cellContent}
                                </Tooltip>
                              ) : (
                                <></>
                              )
                            }
                          >
                            <span
                              className={`d-inline-block ${!isLogTable ? "text-truncate" : ""
                                }`}
                              style={{
                                maxWidth: isLogTable ? "none" : "90%",
                                cursor: isLogTable ? "default" : "pointer",
                                color: isStatusColumn
                                  ? getStatusColor(cellContent)
                                  : undefined,
                              }}
                            >
                              {cellContent}
                            </span>
                          </OverlayTrigger>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={headers?.length} style={{ textAlign: "center" }}>
                  <span className="no_rec_data"> {"No Record Found"}</span>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <DictationModal
        show={showModal}
        onClose={() => setShowModal(false)}
        data={instructionsData}
        position={modalPos}
        selectedRowData={selectedRowData}
      />

      <FloatingTableModal
        show={showModalLogs}
        onClose={() => setShowModalLogs(false)}
        title={modalTitle}
        position={modalPosLog}
        {...modalProps}
      />
    </>
  );
};

export default DoctorDataTable;
